<template>
  <Modal
    v-if="isVisible"
    :show="isVisible"
    :title="t('Bairros de') + ' ' + cidadeName"
    :close-label="t('Fechar')"
    elem-id="modalLocationNeighborhoods"
    @close="close"
    @dismiss="close"
    >
      

    <div>
      <p>{{ t('Selecione os bairros para adicionar à franquia') }}:</p>

      <!-- Search filter -->
      <div class="form-group">
        <label for="searchNeighborhood">{{ t('Procurar') }}:</label>
        <input-text
          data-testid="franquias-locations-neighborhoods-search"
          elem-id="franquias_neighborhoodsSearchFilter"
          id="searchNeighborhood"
          v-model="searchFilter"
          :placeholder="t('Digite para filtrar')"
          style="width: 100%;" />
      </div>

      <!-- Loading state -->
      <inline-message v-if="isLoading" severity="info" :closable="false">
        {{ t('Carregando bairros') }}...
      </inline-message>

      <!-- Bairros list -->
      <table v-else class="table table-hover table-striped table-bordered" style="max-height: 400px; overflow-y: auto;">
        <tbody>
          <tr v-for="bairro in filteredBairros" :key="bairro.ID">
            <td style="width: 75%">
              <i v-if="isAlreadyAdded(bairro.ID)" class="pi pi-check"></i>
              {{ bairro.Name }}
            </td>
            <td style="width: 25%">
              <Button
                :data-testid="`franquias-locations-add-neighborhood-${bairro.ID}`"
                icon="pi pi-plus"
                :label="t('Adicionar bairro')"
                size="small"
                severity="secondary"
                @click="handleAddNeighborhood(bairro.ID)"
                :disabled="isAlreadyAdded(bairro.ID)" />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalidades } from '../../composables/useLocalidades';
import { eventBus } from '../../eventBus';

type Id = number | string;

interface BairroItem {
  ID?: Id;
  Name?: string;
  [key: string]: unknown;
}

interface FranquiaData {
  AreasAtuacao?: {
    Neighborhoods?: Array<{ ID?: Id }>;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface ModalOpenPayload {
  franquiaId?: Id;
  franquiaData?: FranquiaData;
  cidadeId?: Id;
  cidadeName?: string;
}

interface Props {
  franquiaId?: number | null;
  franquiaData?: FranquiaData | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaId: null,
  franquiaData: null,
});

const { t } = useI18n();
const { fetchBairros, toggleRelacao } = useLocalidades();

// Reactive data
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const bairros = ref<BairroItem[]>([]);
const searchFilter = ref<string>('');
const currentCidadeId = ref<Id | null>(null);
const cidadeName = ref<string>('');
const currentFranquiaId = ref<Id | null>(null);
const currentFranquiaData = ref<FranquiaData | null>(null);

// Computed
const filteredBairros = computed<BairroItem[]>(() => {
  if (!searchFilter.value) {
    return bairros.value;
  }
  const filter = searchFilter.value.toLowerCase();
  return bairros.value.filter((bairro) =>
    (bairro.Name || '').toLowerCase().includes(filter),
  );
});

const loadBairros = async (): Promise<void> => {
  if (!currentCidadeId.value) {
    return;
  }

  isLoading.value = true;
  try {
    bairros.value = (await fetchBairros(Number(currentCidadeId.value))) as BairroItem[];
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao carregar bairros'));
  } finally {
    isLoading.value = false;
  }
};

// Methods
const open = async (cidadeId: Id, cidadeNameValue: string): Promise<void> => {
  isVisible.value = true;
  currentCidadeId.value = cidadeId;
  cidadeName.value = cidadeNameValue;
  searchFilter.value = '';
  await loadBairros();
};

const close = (): void => {
  isVisible.value = false;
  searchFilter.value = '';
  bairros.value = [];
  currentCidadeId.value = null;
  cidadeName.value = '';
};

// Event handlers for centralized ModalManager
const handleModalOpen = (data?: ModalOpenPayload): void => {
  if (data?.franquiaId) {
    currentFranquiaId.value = data.franquiaId;
  }
  if (data?.franquiaData) {
    currentFranquiaData.value = data.franquiaData;
  }
  if (data?.cidadeId && data?.cidadeName) {
    void open(data.cidadeId, data.cidadeName);
  }
};

const isAlreadyAdded = (bairroId: Id | undefined): boolean => {
  const franquiaData = currentFranquiaData.value || props.franquiaData;
  if (!franquiaData || !franquiaData.AreasAtuacao) {
    return false;
  }
  const neighborhoods = franquiaData.AreasAtuacao.Neighborhoods || [];
  return neighborhoods.some((neighborhood) => neighborhood.ID === bairroId);
};

const handleAddNeighborhood = async (bairroId: Id | undefined): Promise<void> => {
  const franquiaId = currentFranquiaId.value || props.franquiaId;
  try {
    // NOTE: payload uses PascalCase keys preserved from the legacy JS code;
    // see FranquiasLocationsTab.vue for the documented latent contract mismatch.
    const relacaoData = {
      Type: 'neighborhood',
      LocalidadeID: bairroId,
      OwnerID: franquiaId,
      OwnerType: 'franquia',
    };

    await toggleRelacao(relacaoData as unknown as Parameters<typeof toggleRelacao>[0]);

    eventBus.emit('globalMessage.success', t('Bairro adicionado com sucesso'));

    // Emit via eventBus so FranquiasLocationsTab can refresh permissions
    eventBus.emit('location-added', { franquiaId: franquiaId });
    close();
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao adicionar bairro'));
  }
};

// Lifecycle hooks for centralized ModalManager
onMounted(() => {
  eventBus.on('modal.locations-neighborhoods.open', handleModalOpen);
  eventBus.on('modal.locations-neighborhoods.close', close);
});

onBeforeUnmount(() => {
  eventBus.off('modal.locations-neighborhoods.open', handleModalOpen);
  eventBus.off('modal.locations-neighborhoods.close', close);
});

// Expose methods for parent
defineExpose({
  open,
  close,
});
</script>

<style scoped>
.form-group {
  margin-bottom: 15px;
}

table {
  display: block;
  max-height: 400px;
  overflow-y: auto;
}

/* PrimeVue components handle their own styling */
</style>
